<template>
  <div class="product-card-wrapper io-eq-el">
    <!-- Product Recommendation -->
    <ProductCardRecommendation
        :product="product"
        :is-single="isSingle"
    />
    <div
        class="product-card"
        :class="{ recommended: product.isRecommended }"
    >
      <!-- Product Title -->
      <ProductCardTitle :product="product"/>

      <!-- Product Benefits -->
      <ProductCardBenefits
          v-if="!product.isInternet"
          :product="product"
      />
      <ProductCardBenefitsInternet
          v-if="product.isInternet"
          :product="product"
      />

      <!-- Product Prices -->
      <ProductCardPrices
          v-if="!product.isInternet && product.price"
          :product="product"
      />
      <ProductCardPricesInternet
          v-if="product.isInternet && product.price"
          :product="product"
      />

      <!-- Product Details -->
      <div
          v-if="product.price"
          class="product-card__bottom"
      >
        <ProductCardAddons
          v-if="product.isInternet"
          :cart-item-key="cartItemKey"
          :product="product"
        />
        <Expand title="Produktdetails">
          <PriceDetailsTable
              v-if="!product.isInternet"
              :product="product"
              :key="`details-${cardKey}`"
          />
          <PriceDetailsTableInternet
              v-if="product.isInternet"
              :product="product"
              :key="`details-internet-${cardKey}`"
          />
        </Expand>
        <BaseButton
            v-if="!preview"
            link-type="button"
            width="full"
            :style="{
            background: `linear-gradient(90deg, ${branch?.customColor.primary}, ${branch?.customColor.secondary})`,
          }"
            text-align="center"
            @click.prevent="emitOrder"
        >
          {{ product.isInternet ? 'Tarif auswählen' : 'In den Warenkorb' }}
        </BaseButton>
      </div>
      <div
          v-if="showCalculator"
          class="product-card__bottom product-card__bottom--calculator"
      >
        <ProductCardCalculator
            :branch="product.branch"
            :product="product"
            :button-label="product.isInternet ? 'Verfügbarkeit prüfen' : 'Preis berechnen'"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import BaseButton from '@/components/BaseButton.vue';
import PriceDetailsTableInternet from '@/components/product/PriceDetailsTableInternet.vue';
import ProductCardAddons from '@/components/product/product-card/ProductCardAddons.vue';
import ProductCardBenefits from '@/components/product/product-card/ProductCardBenefits.vue';
import ProductCardBenefitsInternet from '@/components/product/product-card/ProductCardBenefitsInternet.vue';
import ProductCardCalculator from '@/components/product/product-card/ProductCardCalculator.vue';
import ProductCardPrices from '@/components/product/product-card/ProductCardPrices.vue';
import ProductCardPricesInternet from '@/components/product/product-card/ProductCardPricesInternet.vue';
import ProductCardRecommendation from '@/components/product/product-card/ProductCardRecommendation.vue';
import ProductCardTitle from '@/components/product/product-card/ProductCardTitle.vue';
import BranchInterface from '@/interfaces/BranchInterface';
import {
  onBeforeUnmount,
  onMounted,
  ref,
  computed,
} from 'vue';
import Expand from '@/components/Expand.vue';
import PriceDetailsTable from '@/components/product/PriceDetailsTable.vue';
import { getRandomId } from '@/helpers';
import Product from '@/classes/Product';
import { useStore } from 'vuex';

const emit = defineEmits(['order', 'cart']);
const props = defineProps<{
  product: Product
  isSingle?: boolean
  preview?: boolean
  showCalculator?: boolean
  cartItemKey?: string
}>();

const store = useStore();
const colLeft = ref<InstanceType<typeof HTMLElement>>();
const colRight = ref<InstanceType<typeof HTMLElement>>();
const btnSpacer = ref<InstanceType<typeof HTMLElement>>();
const cardKey = ref(getRandomId());

const branch = computed<BranchInterface | undefined>(() => store.getters['branches/all'].find((br: BranchInterface) => br.mainCategory.key === props.product.branch));

const emitOrder = () => {
  emit('order', props.product);
};

const setSpacerHeight = () => {
  if (colLeft.value && colRight.value && btnSpacer.value) {
    btnSpacer.value.style.height = '0px';
    const diff = colLeft.value.offsetHeight - colRight.value.offsetHeight;
    btnSpacer.value.style.height = `${diff}px`;
  }
};

onMounted(() => {
  window.addEventListener('resize', setSpacerHeight);
  window.addEventListener('load', setSpacerHeight);
  setSpacerHeight();
  setTimeout(setSpacerHeight, 250);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', setSpacerHeight);
  window.removeEventListener('load', setSpacerHeight);
});
</script>

<style lang="scss">
@import "src/scss/component-helpers";

.product-card {
  box-shadow: 0 3px 6px rgba(0, 0, 0, .15);
  border-radius: 20px;
  background-color: #ffffff;

  @media(min-width: map-get($grid-breakpoints, 'lg')) {
    border-radius: 40px;
  }

  > div + div {
    border-top: 1px solid #ECF2F6;
  }

  .product-card__bottom {
    border-top: none;
    padding: 20px;
    display: grid;
    grid-template-columns: 100%;
    gap: 20px;
    border-radius: 0 0 20px 20px;

    @media(min-width: map-get($grid-breakpoints, 'lg')) {
      padding: 30px 40px 40px;
      border-radius: 0 0 40px 40px;
    }

    &--calculator {
      background-color: #ECF2F6;
    }
  }
}
</style>
